import React from 'react';
import { Box, Grid, Typography, Button, Card, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const WhyChooseUs = () => {
  const theme = useTheme();

  // useInView hook to control animations on scroll in/out
  const [ref, inView] = useInView({
    triggerOnce: false,
    rootMargin: "-50px 0px"
  });

  // Previous container variants (fade and slide up/down)
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 }
    }
  };

  // Enhanced Title Variants (fade in and slide up)
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  // Left grid variants (slide from left)
  const leftVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  // Right grid variants (slide from right)
  const rightVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  // Button variant for a subtle pop-in
  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, type: "spring", stiffness: 200, damping: 15 } }
  };

  // Hover effects
  const leftHover = { scale: 1.03, transition: { duration: 0.3 } };
  const cardHoverVariants = { hover: { scale: 1.02, boxShadow: "0px 8px 25px rgba(0,0,0,0.25)", transition: { duration: 0.3 } } };

  return (
    <Box
      ref={ref}
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      sx={{
        width: "100%",
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
        backgroundColor: "#F7F9FC"
      }}
    >
      <Box sx={{ maxWidth: "1200px", mx: "auto", width: "100%" }}>
        <Grid container spacing={4} alignItems="stretch" sx={{ mx: 0, width: "100%" }}>
          {/* Left Grid */}
          <Grid
            item
            xs={12}
            md={7.8}
            component={motion.div}
            variants={leftVariants}
            whileHover={leftHover}
            sx={{
              maxWidth: { xs: "100%", md: "100%", lg: "66%" },
              mb: { xs: 4, md: 0 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              pl: { xs: 2, md: 0 },
              pr: { xs: 2, md: 4 }
            }}
          >
            <Typography
              component={motion.div}
              variants={titleVariants}
              variant="h4"
              sx={{
                color: "#64B5F6",
                fontWeight: "bold",
                mb: 4,
                textAlign: "left",
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: -10,
                  width: "80px",
                  height: "4px",
                  background: "linear-gradient(to right, #64B5F6, transparent)",
                  borderRadius: "2px"
                }
              }}
            >
              Why Choose our team ?
            </Typography>

            <Typography
              component={motion.div}
              variants={leftVariants}
              sx={{
                color: "#333",
                mb: 3,
                lineHeight: 1.8,
                fontSize: "1rem",
                fontWeight: 400,
                textAlign: "justify",
                hyphens: "auto",
                px: { xs: 2, md: 0 }
              }}
            >
              At MortgageLine, we believe getting a mortgage shouldn't be overwhelming—it should be empowering
            </Typography>

            <Typography
              component={motion.div}
              variants={leftVariants}
              sx={{
                color: "#333",
                mb: 3,
                lineHeight: 1.8,
                fontSize: "1rem",
                fontWeight: 400,
                textAlign: "justify",
                hyphens: "auto",
                px: { xs: 2, md: 0 }
              }}
            >
              What sets our team apart?

            </Typography>
            <Typography
              component={motion.div}
              variants={leftVariants}
              sx={{
                color: "#333",
                mb: 3,
                lineHeight: 1.8,
                fontSize: "1rem",
                fontWeight: 400,
                textAlign: "justify",
                hyphens: "auto",
                px: { xs: 2, md: 0 }
              }}
            >
              We're not here to push products—we're here to build relationships. We take the time to understand your goals, explain your options clearly, and advocate for the best possible terms. 

            </Typography>

            <Typography
              component={motion.div}
              variants={leftVariants}
              sx={{
                color: "#333",
                mb: 3,
                lineHeight: 1.8,
                fontSize: "1rem",
                fontWeight: 400,
                textAlign: "justify",
                hyphens: "auto",
                px: { xs: 2, md: 0 }
              }}
            >
              Backed by a network of trusted lenders and a team that's as responsive as we are knowledgeable, we make your financing process smooth, smart, and even (dare we say) enjoyable.<br  />
            </Typography>
            <Typography
              component={motion.div}
              variants={leftVariants}
              sx={{
                color: "#333",
                mb: 3,
                lineHeight: 1.8,
                fontSize: "1rem",
                fontWeight: 400,
                textAlign: "justify",
                hyphens: "auto",
                px: { xs: 2, md: 0 }
              }}
            >
              Let's make your next move with clarity and confidence—together.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Button 
                component={motion.a}
                whileHover={{ 
                  boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.15)" 
                }}
                whileTap={{ scale: 0.97 }}
                variant="contained"
                href="mailto:rahim@rahimvirani.ca"
                aria-label="Email us to learn more about our mortgage services"
                sx={{
                  bgcolor: "#0056B3",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  borderRadius: "25px",
                  textTransform: "none",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { bgcolor: "#004c69" },
                  textDecoration: 'none'
                }}
              >
                View More
              </Button>
            </Box>


          </Grid>

          {/* Right Grid */}
          <Grid
            item
            xs={12}
            md={4.2}
            component={motion.div}
            variants={rightVariants}
            whileHover={{ scale: 1.03 }}
            sx={{
              maxWidth: { xs: "100%", md: "100%", lg: "31%" },
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", md: "center", lg: "flex-start" },
              px: { xs: 2, md: 0 },
              mx: { xs: "auto", md: "auto", lg: 0 }
            }}
          >
            <Card
              component={motion.div}
              whileHover={cardHoverVariants.hover}
              sx={{
                backgroundColor: "#0056B3",
                color: "white",
                p: { xs: 3, md: 4 },
                borderRadius: 1,
                width: { xs: "100%", sm: "500px", lg: "100%" },
                display: "flex",
                flexDirection: "column",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)"
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon key={star} sx={{ color: "#FFD700", fontSize: "1.5rem" }} />
                ))}
              </Box>

              <Typography variant="h4" sx={{ mb: 6, fontWeight: "bold", textAlign: "center" }}>
                WE ARE PIONEER IN MORTGAGELINE .
              </Typography>

              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Typography variant="h2" sx={{ fontWeight: "bold", mb: 1 }}>
                  18+
                </Typography>
                <Typography sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                  YEARS OF EXPERIENCE
                </Typography>
              </Box>

              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Typography variant="h2" sx={{ fontWeight: "bold", mb: 1 }}>
                  10K+
                </Typography>
                <Typography sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                  CLIENTS SATISFIED
                </Typography>
              </Box>

              <Box sx={{ mb: 6, textAlign: "center" }}>
                <Typography variant="h2" sx={{ fontWeight: "bold", mb: 1 }}>
                  60+
                </Typography>
                <Typography sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                  SKILLED PROFESSIONALS
                </Typography>
              </Box>

              <Button
                component={motion.a}
                variant="contained"
                href="mailto:rahim@rahimvirani.ca"
                aria-label="Contact us for mortgage assistance"
                sx={{
                  bgcolor: "white",
                  color: "#005E83",
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  borderRadius: "25px",
                  width: "200px",
                  alignSelf: "center",
                  mt: "auto",
                  textDecoration: 'none'
                }}
              >
                Contact Us
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default WhyChooseUs;
